'use client';

import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderTabPreview } from './_components/arrival-order-tab-preview.component';
import { SearchCardArrivalOrderModal } from './search-card-arrival-order-modal.component';

import { ArrivalOrderSlotsGrid } from '@/app/(main)/(pages)/_components/schedules/arrival-order/arrival-order-slots-grid.component';
import { UnavailableScheduleModal } from '@/app/(main)/(pages)/_components/schedules/unavailable-schedule-modal/unavailable-schedule-modal.component';
import { SlotsSkeleton } from '@/app/(main)/(pages)/_components/schedules/slots-skeleton.component';
import { useScheduleSlots } from '@/app/(main)/(pages)/_components/schedules/_hooks/use-schedule-slots.hook';
import { useSlotFilters } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-slot-filters.hook';
import {
  ScheduleType,
  SchedulesSearchSlot,
  SearchPartnerData,
} from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import {
  buildSlotCartItems,
  getOfferedProcedures,
} from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/_components/tabs/_utils/slot-cart-items.util';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { ADD_TO_CART_ERROR_MESSAGE, useAddToCart } from '@/hooks/use-add-to-cart.hook';
import { isOnlineConsultationSearch } from '@/app/(main)/(pages)/busca/[slug]/_utils/is-online-consultation-search.util';
import { useCartAdditionConfirmation } from '@/hooks/use-cart-addition-confirmation.hook';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { PatientOption } from '@/types/patient-selection.types';
import { isSchedulePast } from '@/utils/schedule/is-schedule-past.util';

const CARD_ARRIVAL_ORDER_DAYS = 3;

const logger = getLogger('SearchCardTabArrivalOrder');

interface SearchCardTabArrivalOrderProps {
  schedule: SearchPartnerData;
  scheduleType?: ScheduleType;
}

export function SearchCardTabArrivalOrder({
  schedule,
  scheduleType = ScheduleType.ARRIVAL_ORDER,
}: SearchCardTabArrivalOrderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldRestoreSlotsModal, setShouldRestoreSlotsModal] = useState(false);
  const [isUnavailableScheduleOpen, setIsUnavailableScheduleOpen] = useState(false);
  const [busySlotIds, setBusySlotIds] = useState<ReadonlySet<string>>(() => new Set());
  const selectedSchedule = schedule.schedulesTypes[scheduleType]?.[0];
  const scheduleId = selectedSchedule?.id;
  const slotFilters = useSlotFilters();
  const { requestPatientSelection } = usePatientSelection();
  const { addToCart } = useAddToCart();
  const { confirmCartAddition } = useCartAdditionConfirmation();
  const { toast } = useToast();

  const { isLoading, days, hasMore, offset, goNext, goPrev, refresh } = useScheduleSlots({
    scheduleId,
    limitDays: CARD_ARRIVAL_ORDER_DAYS,
    filters: slotFilters,
  });

  const partnerId = schedule.partner.id;
  const partnerFacilityId = schedule.partner.facility.id;
  const price = sumProcedurePrices(schedule.procedures);
  const openModal = () => setIsModalOpen(true);

  const ageRange = scheduleType === ScheduleType.LABORATORIAL
    ? {}
    : { minAge: selectedSchedule?.minAge, maxAge: selectedSchedule?.maxAge };

  const holdSlot = async (slotId: string, run: () => Promise<unknown>) => {
    setBusySlotIds((current) => new Set(current).add(slotId));

    try {
      await run();
    } finally {
      setBusySlotIds((current) => {
        const remaining = new Set(current);

        remaining.delete(slotId);

        return remaining;
      });
    }
  };

  const handleSelectPatient = async (slot: SchedulesSearchSlot, patient: PatientOption) => {
    const unpricedProcedures = getOfferedProcedures(schedule).filter((procedure) => !procedure.price);

    if (unpricedProcedures.length > 0) {
      logger.error('Blocked an arrival-order purchase because an offered procedure has no price', {
        partnerId,
        partnerFacilityId,
        procedureIds: unpricedProcedures.map((procedure) => procedure.id),
        slotId: slot.id,
      });
      toast.error(ADD_TO_CART_ERROR_MESSAGE);

      return;
    }

    await holdSlot(slot.id, () => addToCart(buildSlotCartItems(schedule, patient.id, slot.id)));
  };

  const handleSelectSlot = (slot: SchedulesSearchSlot) => {
    if (busySlotIds.has(slot.id)) {
      return;
    }

    const wasSlotsModalOpen = isModalOpen;

    setShouldRestoreSlotsModal(wasSlotsModalOpen);
    setIsModalOpen(false);

    if (isSchedulePast(slot.end)) {
      setIsUnavailableScheduleOpen(true);

      return;
    }

    requestPatientSelection({
      ageRange,
      onBeforeSelection: async () => {
        if (isSchedulePast(slot.end)) {
          setIsUnavailableScheduleOpen(true);

          return false;
        }

        const confirmed = await confirmCartAddition(
          {
            partnerId,
            facility: schedule.partner.facility,
            isOnline: isOnlineConsultationSearch(schedule),
          },
          scheduleType,
          { isStandalone: false },
        );

        if (!confirmed) {
          setIsModalOpen(wasSlotsModalOpen);

          return false;
        }

        if (isSchedulePast(slot.end)) {
          setIsUnavailableScheduleOpen(true);

          return false;
        }

        return true;
      },
      onSelectPatient: (patient) => handleSelectPatient(slot, patient),
    });
  };

  const handleUnavailableScheduleClose = () => {
    setIsUnavailableScheduleOpen(false);
    setIsModalOpen(shouldRestoreSlotsModal);
    refresh();
  };

  return (
    <div data-testid={DataTestIdEnum.SEARCH_CARD_TAB_ARRIVAL_ORDER}>
      {isLoading
        ? (
            <>
              <SlotsSkeleton variant="card" className="xl:hidden" />
              <SlotsSkeleton variant="modal" columns={CARD_ARRIVAL_ORDER_DAYS} className="hidden xl:flex" />
            </>
          )
        : (
            <>
              <ArrivalOrderTabPreview
                day={days[0]}
                partnerId={partnerId}
                price={price}
                busySlotIds={busySlotIds}
                className="xl:hidden"
                onSeeMore={openModal}
                onSelectSlot={handleSelectSlot}
              />
              <ArrivalOrderSlotsGrid
                days={days}
                hasMore={hasMore}
                offset={offset}
                partnerId={partnerId}
                price={price}
                busySlotIds={busySlotIds}
                className="hidden xl:flex"
                onPrev={goPrev}
                onNext={goNext}
                onSeeMore={openModal}
                onSelectSlot={handleSelectSlot}
              />
            </>
          )}

      <SearchCardArrivalOrderModal
        scheduleId={scheduleId}
        schedule={schedule}
        busySlotIds={busySlotIds}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSelectSlot={handleSelectSlot}
      />

      <UnavailableScheduleModal
        open={isUnavailableScheduleOpen}
        onClose={handleUnavailableScheduleClose}
      />
    </div>
  );
}
